import React, { useEffect, useMemo, useState } from 'react';
import {
  NewRestartIcon,
  NewFactoryResetIcon,
  NewCheckForUpdatesIcon,
  UnpairedIcon,
  NewDoneIcon,
} from '../Common/Icons';
import { useUserRole, hasStaffPrivileges, hasOnlyStaffAdminPrivileges } from './helpers';
import NetworkingDetailsBlock from './NetworkingDetailsBlock';
import HC200icon from '../Common/Icons/HC200icon.svg?img';
import { getConfirmationMessage, isDeviceOffline } from './DeviceInventoryModal';
import { Tooltip } from 'antd';
import { StatusInfo } from './DeviceInventoryList';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import RadioDropdown from '../Common/RadioDropdown';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { checkIfTVSourceEditable } from '@/utilities/helpers/commonHelper';

const HC200ModalInfo = ({
  device,
  hubRecord,
  performAction,
  actionsData,
  isTVDevice,
  hideModal,
  ConfirmationModal,
  isActionModalVisible,
  activeDeviceAction,
  handleDeviceActionClick,
  timezone,
  loginUserTimeformat,
  userConfig,
  removeDevice,
  handleCancel,
  setIsSmartCameraModalOpen,
  deviceStatus,
}) => {
  const trans = useSelector(translator);
  const userRole = useUserRole();
  const hc200IconUrl = userConfig?.customer_support?.hc200device_logo_url || null;
  const deviceManagementApi = useSelector((state) => state.apis.deviceManagementApi);

  const allowManageAction = useMemo(
    () =>
      hasStaffPrivileges(userRole) ||
      ['manage', 'care'].includes(hubRecord?.rolename?.toLowerCase()),
    [userRole, hubRecord]
  );

  const allowFactoryReset = useMemo(
    () =>
      hasOnlyStaffAdminPrivileges(userRole) &&
      ['manage', 'care', 'social', ''].includes(hubRecord?.rolename?.toLowerCase()),
    [userRole, hubRecord]
  );

  const allowRemoveDevice = useMemo(
    () =>
      (hasStaffPrivileges(userRole) && hubRecord?.rolename?.toLowerCase() === 'manage') ||
      (hasOnlyStaffAdminPrivileges(userRole) &&
        ['manage', 'care', 'social', ''].includes(hubRecord?.rolename?.toLowerCase())),
    [userRole, hubRecord]
  );

  const showActionButtons =
    (allowManageAction || hasStaffPrivileges(userRole)) && device?.paired_status !== 'UNPAIRED';

  const handleRestart = () => {
    const data = {
      ...actionsData.restart_hc200,
      deviceUuid: device.deviceuuid,
    };
    performAction(data);
    hideModal('restart_hc200');
  };

  const handleFactoryReset = () => {
    const data = {
      ...actionsData.factory_reset_hc200,
      deviceUuid: device.deviceuuid,
    };
    performAction(data);
    hideModal('factory_reset_hc200');
  };

  const handleCheckForUpdates = () => {
    const data = {
      ...actionsData.check_for_updates_hc200,
      deviceUuid: device.deviceuuid,
    };
    performAction(data);
    hideModal('check_for_updates_hc200');
  };

  const handleRemoveDevice = () => {
    removeDevice(device);
    hideModal('remove_device');
    handleCancel();
    setIsSmartCameraModalOpen(false);
  };

  const devicePreferredDeviceType = isTVDevice?.device_preferred_device_type || '-';
  const devicePreferredHdmiDevicePort =
    devicePreferredDeviceType && String(devicePreferredDeviceType).toUpperCase() === 'HDMI'
      ? ' - ' + isTVDevice?.device_preferred_hdmi_device_port
      : '';

  // Define the constant map
  const tvConfigMap = isTVDevice
    ? [
        {
          key: trans('MANUFACTURER'),
          value: isTVDevice?.device_manufacture_name || '-',
        },
        { key: trans('MODEL'), value: isTVDevice?.device_tv_model || '-' },
        {
          key: trans('CONEECTEDTO'),
          value: isTVDevice?.device_connected_hdmi_port
            ? `${trans('HDMI_U')}-${isTVDevice.device_connected_hdmi_port}`
            : '-',
        },
        { key: trans('RESOLUTION'), value: isTVDevice?.device_resolution || '-' },
        { key: trans('CEC_U'), value: isTVDevice?.device_cec_status || '-' },
        {
          key: trans('CONTROLMODE'),
          value:
            !isTVDevice?.device_learning_type || isTVDevice?.device_learning_type === 'NONE'
              ? 'Not set'
              : `${isTVDevice?.device_learning_type} (${devicePreferredDeviceType + devicePreferredHdmiDevicePort})`,
        },
      ]
    : [];

  // TV Source dropdown state and options (flat list for CustomDropdown)
  const tvSourceOptions = useMemo(
    () => [
      { key: 'not-set', label: `${trans('NOT_SET')}` },
      { key: 'hdmi-in', label: `${trans('HDMI_U')}-IN` },
      { key: 'hdmi-1', label: `${trans('HDMI_U')}-1` },
      { key: 'hdmi-2', label: `${trans('HDMI_U')}-2` },
      { key: 'hdmi-3', label: `${trans('HDMI_U')}-3` },
      { key: 'hdmi-4', label: `${trans('HDMI_U')}-4` },
      { key: 'hdmi-5', label: `${trans('HDMI_U')}-5` },
      { key: 'live-tv', label: `${trans('SMART_TV_TV_TUNER')}` },
      { key: 'single-ir', label: `${trans('MANUAL_TV')}` },
    ],
    [trans]
  );

  const initialTvSourceSelected = useMemo(() => {
    return [isTVDevice?.preferred_input_source?.toLowerCase() ?? 'not-set'];
  }, [isTVDevice]);

  const [tvSourceSelected, setTvSourceSelected] = useState(initialTvSourceSelected);

  useEffect(() => {
    setTvSourceSelected(initialTvSourceSelected);
  }, [initialTvSourceSelected]);

  const handleTvSourceChange = (key) => {
    setTvSourceSelected([key]);
    const deviceUuid = device?.deviceuuid;
    const payload = {
      set_preferred_input: {
        preferred_input: key.toUpperCase(),
      },
    };

    const errorCallback = (error) => {
      console.error('Error TV Source:' + key + ' ' + error.message);
    };

    const successCallback = (info) => {
      // Handle success response if needed
    };

    try {
      deviceManagementApi.setPreferredSource(
        deviceUuid,
        payload,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const selectedTvSourceLabel = useMemo(() => {
    const selKey = tvSourceSelected?.[0];
    if (!selKey) return '';
    const item = tvSourceOptions.find((c) => c.key === selKey);
    return item?.label || '';
  }, [tvSourceSelected, tvSourceOptions]);

  return (
    <>
      <div className="hc200-container-row-1">
        {showActionButtons && (
          <div className="actions-row">
            <div className="hc200-container">
              <div className="hc200-actions-block">
                <div className="device-action-label">{trans('ACTIONS')}</div>
                <div className="action-description-container">
                  {allowFactoryReset && (
                    <div className="action-description-row">
                      <span className="device-action-row-label">{trans('“FACTORYRESET”')}</span>
                      <span className="device-action-row-description">
                        {trans('INITIATESAFACTORYRESETBYCICKINGONBUTTON')}
                      </span>
                    </div>
                  )}
                  {allowManageAction && (
                    <div className="action-description-row">
                      <span className="device-action-row-label">{trans('“RESTART”')}</span>
                      <span className="device-action-row-description">
                        {trans('RESTARTTHESMARTCAMERABYCLICKINGONBUTTON')}
                      </span>
                    </div>
                  )}
                  <div className="action-description-row">
                    <span className="device-action-row-label">{trans('“CHECKFORUPDATES”')}</span>
                    <span className="device-action-row-description">
                      {trans('PERFORMCHECKFORUPDATESBYCLICKINGONBUTTON')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="action-bottom-buttons">
                <div className="action-buttons">
                  {allowRemoveDevice && (
                    <span>
                      <div onClick={() => handleDeviceActionClick('remove_device')}>
                        <div className="factory-reset-icon-label">{trans('REMOVEDEVICE')}</div>
                      </div>
                    </span>
                  )}
                  {allowFactoryReset && (
                    <Tooltip
                      title={
                        isDeviceOffline(device) ? (
                          <StatusInfo
                            lastConnectTime={device?.lastconnecttime}
                            lastDisconnectTime={device?.lastdisconnecttime}
                            device={device}
                            timezone={timezone}
                            loginUserTimeformat={loginUserTimeformat}
                            showDuration={false}
                            tooltipMode={true}
                            trans={trans}
                          />
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <div
                          onClick={() =>
                            !isDeviceOffline(device) &&
                            handleDeviceActionClick('factory_reset_hc200')
                          }
                          className={`${
                            isDeviceOffline(device) ? 'device-page-actions-disabled' : ''
                          }`}
                        >
                          <div className="factory-reset-icon-label">{trans('FACTORYRESET_L')}</div>
                        </div>
                      </span>
                    </Tooltip>
                  )}
                  <div className="button-container">
                    <Tooltip
                      title={
                        isDeviceOffline(device) ? (
                          <StatusInfo
                            lastConnectTime={device?.lastconnecttime}
                            lastDisconnectTime={device?.lastdisconnecttime}
                            device={device}
                            timezone={timezone}
                            loginUserTimeformat={loginUserTimeformat}
                            showDuration={false}
                            tooltipMode={true}
                            trans={trans}
                          />
                        ) : (
                          ''
                        )
                      }
                    >
                      <div
                        className="button-block"
                        style={{
                          visibility: allowManageAction ? 'visible' : 'hidden',
                          display: allowManageAction ? 'block' : 'none',
                        }}
                      >
                        <span className="check-for-updates-label">
                          <div
                            className={`device-action-icon-label ${
                              isDeviceOffline(device) ? 'device-page-actions-disabled' : ''
                            }`}
                            onClick={() =>
                              !isDeviceOffline(device) &&
                              handleDeviceActionClick('check_for_updates_hc200')
                            }
                          >
                            <NewCheckForUpdatesIcon
                              className={`new-check-for-updates-icon ${
                                isDeviceOffline(device) ? 'device-page-actions-disabled' : ''
                              }`}
                            />
                            <div className="check-for-updates-label">
                              {trans('CHECKFORUPDATES')}
                            </div>
                          </div>
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                  {allowManageAction && (
                    <Tooltip
                      title={
                        isDeviceOffline(device) ? (
                          <StatusInfo
                            lastConnectTime={device?.lastconnecttime}
                            lastDisconnectTime={device?.lastdisconnecttime}
                            device={device}
                            timezone={timezone}
                            loginUserTimeformat={loginUserTimeformat}
                            showDuration={false}
                            tooltipMode={true}
                            trans={trans}
                          />
                        ) : (
                          ''
                        )
                      }
                    >
                      <span>
                        <div
                          className={`custom-yes-button ${
                            isDeviceOffline(device) ? 'device-page-actions-disabled' : ''
                          }`}
                          onClick={() =>
                            !isDeviceOffline(device) && handleDeviceActionClick('restart_hc200')
                          }
                        >
                          <NewRestartIcon
                            className={`new-restart-icon ${
                              isDeviceOffline(device) ? 'device-page-actions-disabled' : ''
                            }`}
                          />
                          <div className="hc200-action-label">{trans('RESTART')}</div>
                        </div>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="hc200-container-row-2">
          <div className="hc200-column-1">
            <div className="hc200-actions-container-column-1">
              <div className="hc200-networking-container">
                <div className="hc200-networking-block">
                  <div className="networking-label">
                    <span className="nw-label">{trans('NETWORKING')}</span>
                  </div>
                  <NetworkingDetailsBlock
                    WANType={device?.device_primary_wan}
                    device={device}
                    deviceStatus={deviceStatus}
                  />
                </div>
              </div>
              <div className="hc200-tv-container">
                <div className="hc200-tv-block">
                  <div className="tv-label">
                    <span className="hc200-tv-label">{trans('TVINFORMATION')}</span>
                  </div>
                  <div className="tv-details-container">
                    <div className="done-block">
                      {isTVDevice?.device_learning_type === null ||
                      isTVDevice?.device_learning_type === undefined ||
                      isTVDevice?.device_learning_type === 'NONE' ? (
                        <UnpairedIcon label={trans('NOTDONE')} />
                      ) : (
                        <NewDoneIcon label={trans('DONE')} />
                      )}
                    </div>
                    {tvConfigMap.map((item, index) => {
                      const isValueLong = item.value.length > 25;
                      const displayValue = isValueLong
                        ? `${item.value.slice(0, 25)}...`
                        : item.value;
                      return (
                        <div key={index} className="tv-info-row">
                          <span className="tv-info-key">{item.key}:</span>
                          <Tooltip title={item.value.length > 25 ? item.value : ''} placement="top">
                            <span className="tv-info-value">{displayValue}</span>
                          </Tooltip>
                        </div>
                      );
                    })}
                    <div key={2} className="tv-info-row">
                      <span className="tv-info-source-key">
                        {trans('TVSOURCE') || 'TV Source'}:
                      </span>
                      <RadioDropdown
                        menuItems={tvSourceOptions}
                        selectedKeys={tvSourceSelected}
                        handleChange={handleTvSourceChange}
                        disabled={
                          isDeviceOffline(device) || !checkIfTVSourceEditable(userRole, hubRecord)
                        }
                      >
                        <span className="tv-info-value">
                          {selectedTvSourceLabel || trans('SELECT') || 'Select'}
                        </span>
                      </RadioDropdown>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="hc200-column-2">
            <div className="hc200-block">
              <div className="device-model-label">
                {hc200IconUrl
                  ? trans('HOMEWATCHCONNECTSMARTCAMERA')
                  : trans('HOMESIGHTSMARTCAMERA')}
              </div>
              <div className="hc200-device-icon">
                {hc200IconUrl ? (
                  <img src={hc200IconUrl} alt="Homewatch Connect Wellness Hub" />
                ) : (
                  <img src={HC200icon} alt="HomeSight Wellness Hub" />
                )}
              </div>
            </div>
          </div>
        </div>

        <ConfirmationModal
          visible={isActionModalVisible[activeDeviceAction]}
          title={
            <span className="custom-modal-title">
              {getConfirmationMessage(actionsData[activeDeviceAction]?.title?.toLowerCase(), trans)}
            </span>
          }
          description={actionsData[activeDeviceAction]?.description}
          onConfirm={() =>
            activeDeviceAction === 'restart_hc200'
              ? handleRestart(device)
              : activeDeviceAction === 'check_for_updates_hc200'
                ? handleCheckForUpdates(device)
                : activeDeviceAction === 'remove_device'
                  ? handleRemoveDevice(device)
                  : handleFactoryReset(device)
          }
          onCancel={() => hideModal(activeDeviceAction)}
          customIcon={
            activeDeviceAction === 'restart_hc200' ? (
              <NewRestartIcon strokeColor="#113E57" fillColor="#113E57" />
            ) : activeDeviceAction === 'check_for_updates_hc200' ? (
              <NewCheckForUpdatesIcon strokeColor="#113E57" fillColor="#113E57" />
            ) : null
          }
          customLabel={actionsData[activeDeviceAction]?.title}
        />
      </div>
    </>
  );
};

export default HC200ModalInfo;
